import React, { useEffect, useState } from "react";
import { fetchActivePlans } from "../../services/planService";
import { fetchActiveSpeakers } from "../../services/adminSpeakerServices";
import { addSessionToDay, updateSession } from "../../services/adminTimeTableServices";
import { extractLocalTime, buildLocalDateTime } from "../../../utils/dateTimeUtils";
import ContentPicker from "./ContentPicker";
import DurationTimePicker, { computeEndTime } from "./DurationTimePicker";

const LEVELS   = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];
const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];

const inp = { width: "100%", padding: "8px 11px", borderRadius: 7, border: "1px solid #e2e8f0", boxSizing: "border-box", marginBottom: 12, fontSize: 14, color: "#0f172a", background: "#fff" };
const lbl = { display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.03em" };

const SessionModal = ({ dayId, dayDate, session, onClose, reload, onSessionUpdated }) => {
  const [plans, setPlans]       = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [form, setForm] = useState({
    topic: "", level: "", planId: "", totalSlots: 1,
    speakerId: "", status: "DRAFT", contentType: "", contentId: null, contentLabel: null,
  });
  const [startClock, setStartClock]   = useState("");
  const [durationHrs, setDurationHrs]   = useState(0);
  const [durationMins, setDurationMins] = useState(30);
  const endClock = computeEndTime(startClock, durationHrs, durationMins);

  useEffect(() => {
    fetchActivePlans().then((r)    => setPlans(r.data || []));
    fetchActiveSpeakers().then((r) => setSpeakers(r.data || []));

    if (session) {
      setForm({
        topic:        session.topic        || "",
        level:        session.level        || "",
        planId:       session.planId       || "",
        totalSlots:   session.totalSlots   || 1,
        speakerId:    session.speakerId    ?? "",
        status:       session.status       || "DRAFT",
        contentType:  session.contentType  || "",
        contentId:    session.contentId    || null,
        contentLabel: session.contentLabel || null,
      });
      const st = extractLocalTime(session.startTime);
      const et = extractLocalTime(session.endTime);
      setStartClock(st);
      if (st && et) {
        const [sh, sm] = st.split(":").map(Number);
        const [eh, em] = et.split(":").map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        setDurationHrs(Math.floor(diff / 60));
        setDurationMins(diff % 60);
      }
    }
  }, [session]);

  const handleSubmit = async () => {
    const payload = {
      topic:       form.topic       || null,
      level:       form.level       || null,
      planId:      form.planId      ? Number(form.planId)    : null,
      speakerId:   form.speakerId   ? Number(form.speakerId) : null,
      totalSlots:  Number(form.totalSlots) || 1,
      status:      form.status,
      contentType: form.contentType || null,
      contentId:   form.contentId   || null,
      startTime:   buildLocalDateTime(dayDate, startClock),
      endTime:     buildLocalDateTime(dayDate, endClock),
    };
    try {
      if (session) {
        const res = await updateSession(session.id, payload);
        onSessionUpdated?.(res.data);
      } else {
        await addSessionToDay(dayId, payload);
        reload();
      }
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || "Save failed");
    }
  };

  return (
    <div className="tt-modal-overlay" onClick={onClose}>
      <div className="tt-modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>

        <div className="tt-modal-header">
          <h3>{session ? "Edit Session" : "Add Session"}</h3>
          <button className="tt-drawer-close" onClick={onClose}>✕</button>
        </div>

        <div className="tt-modal-body">

          {/* Date (read-only) */}
          <label style={lbl}>Date</label>
          <input style={{ ...inp, background: "#f8fafc", color: "#64748b" }} value={dayDate} disabled />

          {/* Status */}
          <label style={lbl}>Status</label>
          <select style={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Topic */}
          <label style={lbl}>Topic <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          <input style={inp} placeholder="e.g. Fluency Practice" value={form.topic}
            onChange={(e) => setForm({ ...form, topic: e.target.value })} />

          {/* Level */}
          <label style={lbl}>Level</label>
          <select style={inp} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
            <option value="">— Select Level —</option>
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>

          {/* Times */}
          <div style={{ marginBottom: 12 }}>
            <DurationTimePicker
              startTime={startClock}
              durationHrs={durationHrs}
              durationMins={durationMins}
              endTime={endClock}
              onStartChange={setStartClock}
              onDurationChange={(h, m) => { setDurationHrs(h); setDurationMins(m); }}
              inpStyle={{ marginBottom: 0 }}
            />
          </div>

          {/* Plan */}
          <label style={lbl}>Plan</label>
          <select style={inp} value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })}>
            <option value="">— Select Plan —</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.planName} ({p.planType})</option>)}
          </select>

          {/* Slots */}
          <label style={lbl}>Total Slots</label>
          <input type="number" min={1} style={inp} value={form.totalSlots}
            onChange={(e) => setForm({ ...form, totalSlots: Number(e.target.value) })} />

          {/* Speaker */}
          <label style={lbl}>Speaker <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          <select style={inp} value={form.speakerId}
            onChange={(e) => setForm({ ...form, speakerId: e.target.value ? Number(e.target.value) : "" })}>
            <option value="">— Select Speaker —</option>
            {speakers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          {/* Divider */}
          <div style={{ borderTop: "1px solid #f1f5f9", margin: "4px 0 16px" }} />

          {/* Content Picker */}
          <ContentPicker
            contentType={form.contentType}
            contentId={form.contentId}
            contentLabel={form.contentLabel}
            onTypeChange={(type) => setForm((f) => ({ ...f, contentType: type, contentId: null, contentLabel: null }))}
            onContentChange={(val) => setForm((f) => ({
              ...f,
              contentId:    val?.id    || null,
              contentLabel: val?.label || null,
            }))}
          />

        </div>

        <div className="tt-modal-footer">
          <button className="adm-btn adm-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="adm-btn adm-btn-primary" onClick={handleSubmit}>
            {session ? "Update Session" : "Save Session"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionModal;
